import classNames from 'classnames';
import {
  CSSProperties,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { FontSizeRatio } from '../../styles/Typography';
import { calculateAdjustedPlacement, calculatePositions } from './helpers';
import {
  StyledTooltipContainer,
  StyledTooltipDescription,
} from './StyledTooltip';
import TooltipArrow from './TooltipArrow';
import TooltipContent from './TooltipContent';

interface TooltipProps {
  // Tooltip content.
  content: ReactNode;
  // Where to place the tooltip initially.
  placement?: Placement;
  // Max width of the tooltip. (in pixels, 0 means no max width)
  maxWidth?: number;
  // Custom style for the tooltip.
  style?: CSSProperties;
  // Distance between the tooltip and the element.
  distance?: number;
  // Distance between the arrow and the corner of the tooltip.
  // This should be larger than the tooltip border radius.
  arrowBuffer?: number;
}

export type SimpleDOMRect = Pick<DOMRect, 'top' | 'left' | 'width' | 'height'>;
export type Placement = 'top' | 'bottom' | 'left' | 'right';

const Tooltip = ({
  content,
  placement = 'top',
  maxWidth = 0,
  style,
  distance = Math.round(8 * FontSizeRatio),
  arrowBuffer = Math.round(5 * FontSizeRatio),
  children,
}: PropsWithChildren<TooltipProps>) => {
  // Ref to the element that triggers tooltip.
  const elementRef = useRef<HTMLDivElement>(null);

  // Ref to the tooltip arrow.
  const arrowRef = useRef<HTMLDivElement>(null);

  // Ref to the tooltip content.
  const contentRef = useRef<HTMLDivElement>(null);

  // Bounding rect of the window.
  const [boundingRect, setBoundingRect] = useState<SimpleDOMRect | null>(null);

  // Bounding rect of the whole element.
  const [elementBoundingRect, setElementBoundingRect] =
    useState<DOMRect | null>(null);

  // Bounding rect of the tooltip content.
  const [contentBoundingRect, setContentBoundingRect] =
    useState<DOMRect | null>(null);

  // Adjusted placement of the tooltip.
  // (e.g. if the tooltip cannot be placed within the bounding rect,
  // it will be adjusted to the opposite side.)
  const [adjustedPlacement, setAdjustedPlacement] = useState<Placement | null>(
    null
  );

  // Whether the tooltip is active.
  const [isActive, setIsActive] = useState(false);

  // Stable id linking the trigger to the portalled content. The content is
  // rendered into `#tooltip-portal`, i.e. OUTSIDE the trigger, so without an
  // explicit `aria-describedby` the text is not associated with the control at
  // all -- it computes no description and no name (ISSUE-063).
  // `useId()` returns a value wrapped in colons (`:r0:`), which is NOT a valid
  // CSS identifier -- `querySelector('#:r0:')` throws. `getElementById` copes,
  // but anything reaching for the node with a selector would not, so the
  // colons are stripped rather than left as a trap for the next caller.
  const contentId = `sup-tooltip-${useId().replace(/:/g, '')}`;

  // Sets the bounding rect of the tooltip content.
  useEffect(() => {
    if (!contentRef.current) return;

    setContentBoundingRect(contentRef.current.getBoundingClientRect());
  }, [content, maxWidth]);

  // Determine the actual placement of the tooltip.
  useEffect(() => {
    if (!boundingRect || !contentBoundingRect || !elementBoundingRect) return;

    // Calculates the adjusted placement.
    setAdjustedPlacement(
      calculateAdjustedPlacement(
        placement,
        boundingRect,
        contentBoundingRect,
        elementBoundingRect,

        distance,
        arrowBuffer
      )
    );
  }, [
    boundingRect,
    contentBoundingRect,
    elementBoundingRect,
    placement,
    distance,
    arrowBuffer,
  ]);

  // Sets the position of the tooltip.
  useEffect(() => {
    if (
      !adjustedPlacement ||
      !contentRef.current ||
      !arrowRef.current ||
      !boundingRect ||
      !elementBoundingRect ||
      !contentBoundingRect
    )
      return;

    // Calculates the positions of the tooltip and the arrow.
    const { arrowLeft, arrowTop, tooltipLeft, tooltipTop } = calculatePositions(
      adjustedPlacement,
      boundingRect,
      contentBoundingRect,
      elementBoundingRect,
      distance,
      arrowBuffer
    );

    switch (adjustedPlacement) {
      case 'top': {
        arrowRef.current.style.transform = `translate(${arrowLeft}px, ${Math.floor(
          arrowTop
        )}px)`;
        contentRef.current.style.transform = `translate(${tooltipLeft}px, ${Math.floor(
          tooltipTop
        )}px)`;

        break;
      }

      case 'right': {
        arrowRef.current.style.transform = `translate(${Math.floor(
          arrowLeft
        )}px, ${arrowTop}px)`;
        contentRef.current.style.transform = `translate(${Math.floor(
          tooltipLeft
        )}px, ${tooltipTop}px)`;

        break;
      }

      case 'bottom': {
        arrowRef.current.style.transform = `translate(${arrowLeft}px, ${Math.floor(
          arrowTop
        )}px)`;
        contentRef.current.style.transform = `translate(${tooltipLeft}px, ${Math.floor(
          tooltipTop
        )}px)`;

        break;
      }

      case 'left': {
        arrowRef.current.style.transform = `translate(${Math.floor(
          arrowLeft
        )}px, ${arrowTop}px)`;
        contentRef.current.style.transform = `translate(${Math.floor(
          tooltipLeft
        )}px, ${tooltipTop}px)`;

        break;
      }
    }
  }, [
    boundingRect,
    elementBoundingRect,
    contentBoundingRect,
    adjustedPlacement,
    distance,
    arrowBuffer,
  ]);

  // Shows the tooltip.
  const showTooltip = useCallback(() => {
    // Sets the bounding rect of the window.
    setBoundingRect({
      top: 0,
      left: 0,
      width: window.innerWidth,
      height: window.innerHeight,
    });

    if (!elementRef.current) return;

    const element = elementRef.current as HTMLElement;
    const child = element.firstChild;

    if (!child) throw new Error('Tooltip must have a child element.');

    const childRect = (child as HTMLElement).getBoundingClientRect();

    // Sets the bounding rect of the child element.
    setElementBoundingRect(childRect);

    setIsActive(true);
  }, []);

  // Hides the tooltip.
  const hideTooltip = useCallback(() => {
    setIsActive(false);
  }, []);

  // SC 1.4.13 requires content revealed on hover or focus to be dismissible
  // without moving the pointer or the focus. Escape does exactly that: the
  // trigger keeps focus, only the tooltip goes away.
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape' && isActive) {
        // Stop here rather than letting a parent close a dialog underneath.
        event.stopPropagation();
        hideTooltip();
      }
    },
    [isActive, hideTooltip]
  );

  // `aria-describedby` has to sit on the control itself, not on this wrapper
  // div, or the accessible-description algorithm never reaches it. The single
  // child is an existing contract of this component -- `showTooltip` already
  // throws when there is no child element.
  const describedChild = isValidElement(children)
    ? cloneElement(children as ReactElement, {
        'aria-describedby': contentId,
      })
    : children;

  return (
    <>
      <StyledTooltipContainer
        ref={elementRef}
        className={classNames(
          'sup-tooltip-container',
          `sup-tooltip-${placement}`
        )}
        onPointerEnter={showTooltip}
        onPointerLeave={hideTooltip}
        // React maps onFocus/onBlur to focusin/focusout, which bubble, so a
        // focus landing on the child button is caught here. Without this the
        // tooltip was pointer-only and a keyboard user never saw it (SC 2.1.1).
        onFocus={showTooltip}
        onBlur={hideTooltip}
        onKeyDown={handleKeyDown}
        style={style}
      >
        {describedChild}
        {/* The description the trigger points at. Separate from the visible
            tooltip because that one is `visibility: hidden` at rest and is
            therefore not in the accessibility tree -- see
            StyledTooltipDescription. */}
        <StyledTooltipDescription id={contentId} role="tooltip">
          {content}
        </StyledTooltipDescription>
      </StyledTooltipContainer>
      <TooltipContent
        ref={contentRef}
        isActive={isActive}
        placement={adjustedPlacement}
        maxWidth={maxWidth}
      >
        {content}
      </TooltipContent>
      <TooltipArrow
        ref={arrowRef}
        isActive={isActive}
        placement={adjustedPlacement}
      />
    </>
  );
};

export default Tooltip;
